import { Lci3Report } from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { CampaignNameEditor } from '@features/lci-report/components/CampaignNameEditor';
import {
  DimensionFields,
  DimensionFieldsProps,
} from '@features/lci-report/components/DimensionFields';
import { HeaderWithButton } from '@features/lci-report/components/HeaderWithButton';
import { ImageInput } from '@features/lci-report/components/ImageInput';
import { IncludedSlidesForm } from '@features/lci-report/components/IncludedSlidesForm';
import { LciReportFieldInput } from '@features/lci-report/components/LciReportFieldInput/LciReportFieldInput';
import { Nav } from '@features/lci-report/components/Nav';
import { TagsInfoIcon } from '@features/lci-report/components/TagsInfoIcon';
import {
  DataType,
  getCustomFieldKeys,
  getFieldPath,
} from './customDimensionFields';
import {
  getFtaKeysDefaultText,
  getFtaSummaryDefaultText,
  getRsaSummaryDefaultText,
} from '../subdomains/visitation/default-text';
import { ForwardRefIcon } from '@components/shared/icons/ForwardRefIcon';
import {
  Lci3DimensionTableData,
  TOP_BOTTOM_NAMES,
  computeDimensionSelection,
} from '@features/lci-report';
import { presentationToFile, Presentation } from '@features/powerpoint';
import {
  Subdimension,
  YoutubeImpressionsInput,
  getYoutubeProjectionFactor,
  transformCalculatedFields,
  transformSalesMetrics,
  transformSubdimensions,
  transformVisitMetrics,
} from '@features/reports/subdomains/visitation';
import { DimensionSlug } from '@features/reports';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { TextField } from '@mui/material';
import { getImageInfoFromBase64 } from '@features/powerpoint';
import { ndBlueDarkest } from '@utils/colorGenerator';
import { useLocalStorage } from '@utils/hooks';
import { ComponentProps, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { DimensionSelector } from './DimensionSelector';
import { sortNullable } from '@utils/arrays';
import { LciReportFieldsHook } from '../hooks/useLciReportFields';
import { uniq } from 'lodash';
import { apiSlugToDimensionName } from '@components/Dashboard/dashboardSlice/responseMappers/dimensionStats';
import { BuildPresentationProps } from '../subdomains/visitation';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormSchema, formSchema } from './form-schema';
import { defaultValues } from './default-values';
import { enqueueSnackbar } from 'notistack';

const SUBDIMENSIONS_PER_PAGE = 8;

type LciReportParams = { id: string };

type ActionableRecommendation = { keyLearning: string; action: string };

const iconButtonStyles: ComponentProps<typeof Box>['sx'] = {
  border: `2px solid ${ndBlueDarkest}`,
  color: ndBlueDarkest,
  width: '30px',
  height: '30px',
  fontSize: '1.2rem',
  alignItems: 'center',
  alignSelf: 'end',
};

export type SubdimensionDictionary = Record<string, string>;

const topBottomNameFromIndex = (index: number, total: number) => {
  if (index < SUBDIMENSIONS_PER_PAGE) return TOP_BOTTOM_NAMES[0];
  if (index >= total - SUBDIMENSIONS_PER_PAGE) return TOP_BOTTOM_NAMES[1];
  return null;
};

export type CustomFields = FormSchema;

const getDimensionsByType = (
  dimensions: DimensionFieldsProps<CustomFields>['selectedDimensions'][number]['dimensionName'][],
  type: DataType
): DimensionFieldsProps<CustomFields>['selectedDimensions'] =>
  dimensions.map(dimensionName => {
    const keys = getCustomFieldKeys(dimensionName);

    return { headerId: keys[type], dimensionName };
  });

export const buildDimensionTableData = (
  selectedDimensionStats: Subdimension[] | undefined
): Lci3DimensionTableData => {
  const sortedDimensionStats = selectedDimensionStats?.sort((a, b) =>
    sortNullable(
      a.visitMetrics.optimizationIndex,
      b.visitMetrics.optimizationIndex
    )
  );
  const totalDimensions = sortedDimensionStats?.length ?? 0;
  const dimensionTableData =
    selectedDimensionStats?.reduce<Record<string, Lci3Report['subdimensions']>>(
      (accumulator, dimensionStat, index) => {
        const dimensionName = dimensionStat.name;
        const topBottomName = topBottomNameFromIndex(index, totalDimensions);
        const assignValuesByName = (name: string) => {
          accumulator[name] ??= [];
          accumulator[name] = [...accumulator[name], dimensionStat];
        };
        assignValuesByName(dimensionName);

        if (topBottomName) {
          assignValuesByName(topBottomName);
        }

        return accumulator;
      },
      {}
    ) ?? {};

  return dimensionTableData;
};

type TransformedProps = Pick<
  BuildPresentationProps,
  'visitMetrics' | 'salesSummary' | 'subdimensions' | 'calculatedFields'
>;

const transformPropsForYoutube = (
  props: TransformedProps,
  youtubeImpressions: number | null
): Pick<
  BuildPresentationProps,
  'visitMetrics' | 'salesSummary' | 'subdimensions' | 'calculatedFields'
> => {
  const youtubeProjectionFactor = getYoutubeProjectionFactor(
    youtubeImpressions,
    props.visitMetrics.impressions
  );

  if (!youtubeProjectionFactor) return props;

  return {
    visitMetrics: transformVisitMetrics(
      props.visitMetrics,
      youtubeProjectionFactor
    ),
    salesSummary: transformSalesMetrics(
      props.salesSummary,
      youtubeProjectionFactor
    ),
    subdimensions: transformSubdimensions(
      props.subdimensions,
      youtubeProjectionFactor
    ),
    calculatedFields: transformCalculatedFields(
      props.calculatedFields,
      youtubeProjectionFactor
    ),
  };
};

type LciReportSetupFormProps<TBuildProps extends Record<string, unknown>> =
  NonNullable<LciReportFieldsHook['data']> & {
    buildPresentation: (props: TBuildProps) => Presentation;
    buildPresentationProps?: Partial<TBuildProps>;
    renderVisitAttributionSection?: () => React.ReactNode;
  };

export const LciReportSetupForm = <TBuildProps extends Record<string, unknown>>(
  props: LciReportSetupFormProps<TBuildProps>
) => {
  const {
    campaign,
    selectedReportDate,
    visitMetrics,
    salesSummary: salesAttributionSummary,
    subdimensions,
    campaignAnalyticsReport,
    calculatedFields,
    buildPresentation,
    buildPresentationProps = {},
    renderVisitAttributionSection,
    hasVisitMetrics,
  } = props;
  const params = useParams<LciReportParams>() as LciReportParams;
  const campaignId = Number.parseInt(params.id);
  const [actionableRecommendations, setActionableRecommendations] = useState<
    ActionableRecommendation[]
  >([]);
  const [logoImage, setLogoImage] = useState<string | null>(null);
  const [subdimensionDictionary, setSubdimensionDictionary] =
    useLocalStorage<SubdimensionDictionary>(
      `subdimensionNames-${campaignId}`,
      {}
    );

  const methods = useForm<CustomFields>({
    defaultValues,
    resolver: zodResolver(formSchema(visitMetrics.impressions)),
  });
  const { register, setValue, watch, getValues } = methods;

  const campaignName = watch('campaignName');

  const setDefaultSummaries = () => {
    const {
      visitMetrics: transformedVisitMetrics,
      salesSummary: transformedSalesSummary,
    } = transformPropsForYoutube(
      {
        visitMetrics,
        salesSummary: salesAttributionSummary,
        subdimensions,
        calculatedFields,
      },
      getValues('youtubeImpressions')
    );
    setValue(
      'ftaSummary',
      getFtaSummaryDefaultText(transformedVisitMetrics, campaignName)
    );

    if (!transformedSalesSummary) {
      return;
    }

    setValue(
      'rsaSummary',
      getRsaSummaryDefaultText(
        transformedVisitMetrics,
        transformedSalesSummary,
        campaignName
      )
    );
  };

  const setDefaultFtaKeyTakeaways = () => {
    const { visitMetrics: transformedVisitMetrics } = transformPropsForYoutube(
      {
        visitMetrics,
        salesSummary: salesAttributionSummary,
        subdimensions,
        calculatedFields,
      },
      getValues('youtubeImpressions')
    );
    setValue(
      'ftaOverallResults',
      getFtaKeysDefaultText(transformedVisitMetrics, campaignName)
    );
  };

  const setDefaultRsaKeyTakeaways = () => {
    if (!salesAttributionSummary) {
      console.warn('No sales attribution summary available');
      return;
    }

    const {
      visitMetrics: transformedVisitMetrics,
      salesSummary: transformedSalesSummary,
    } = transformPropsForYoutube(
      {
        visitMetrics,
        salesSummary: salesAttributionSummary,
        subdimensions,
        calculatedFields,
      },
      getValues('youtubeImpressions')
    );

    if (!transformedSalesSummary) {
      console.warn('Expected transformedSalesSummary to be present');
      return;
    }

    setValue(
      'rsaOverallResults',
      getRsaSummaryDefaultText(
        transformedVisitMetrics,
        transformedSalesSummary,
        campaignName
      )
    );
  };

  useEffect(() => {
    if (!campaign) return;

    setValue('campaignName', campaign.name);
  }, [campaign, setValue]);

  const availableDimensionSlugs: DimensionSlug[] = uniq(
    subdimensions.map(subdimension => subdimension.name as DimensionSlug)
  );

  const selectedDimensionSlugs = availableDimensionSlugs.filter(dimensionSlug =>
    watch(`dimensions.${dimensionSlug}.checkbox`, true)
  );

  const { selectedSubdimensions } = computeDimensionSelection(
    subdimensions,
    selectedDimensionSlugs,
    apiSlugToDimensionName
  );

  const downloadPowerPoint = methods.handleSubmit(
    async customFields => {
      const { youtubeImpressions } = customFields;
      const youtubeMeasurementEnabled = campaign?.youtubeMeasurementEnabled;

      const subdimensionsFromDictionary = selectedSubdimensions.map(
        subdimension => ({
          ...subdimension,
          value:
            subdimensionDictionary[subdimension.value] ?? subdimension.value,
        })
      );

      const transformableProps: TransformedProps = {
        visitMetrics,
        salesSummary: salesAttributionSummary,
        subdimensions: subdimensionsFromDictionary,
        calculatedFields,
      };

      const transformedProps: TransformedProps = youtubeMeasurementEnabled
        ? transformPropsForYoutube(transformableProps, youtubeImpressions)
        : transformableProps;

      const brandLogoWithMetadata = logoImage
        ? await getImageInfoFromBase64(logoImage)
        : null;

      const presentationProps = {
        campaign,
        selectedReportDate,
        brandLogo: brandLogoWithMetadata,
        customFields,
        dimensionTableData:
          'dimensionTableData' in buildPresentationProps
            ? buildPresentationProps.dimensionTableData
            : buildDimensionTableData(transformedProps.subdimensions),
        campaignAnalyticsReport,
        ...transformedProps,
        ...buildPresentationProps,
      };

      void presentationToFile(
        buildPresentation(presentationProps as unknown as TBuildProps)
      );
    },
    errors => {
      console.error('Invalid form data', errors);
      enqueueSnackbar(
        'Failed to download presentation. Please check form for errors.',
        { variant: 'error' }
      );
    }
  );

  const removeActionableRecommendation = () => {
    setActionableRecommendations(actionableRecommendations.slice(0, -1));
  };

  const addActionableRecommendation = () => {
    setActionableRecommendations([
      ...actionableRecommendations,
      { keyLearning: '', action: '' },
    ]);
  };

  const onActionableRecommendationChange = (
    currentIndex: number,
    text: string,
    keyLearning?: boolean
  ) => {
    const updatedRecommendations = actionableRecommendations.map(
      (recommendation, index) =>
        index === currentIndex
          ? {
              ...recommendation,
              ...(keyLearning ? { keyLearning: text } : { action: text }),
            }
          : recommendation
    );
    setActionableRecommendations(updatedRecommendations);
  };

  const dimensions = [...TOP_BOTTOM_NAMES, ...selectedDimensionSlugs];

  const ftaDimensions = getDimensionsByType(dimensions, 'fta');
  const rsaDimensions = getDimensionsByType(dimensions, 'rsa');

  return (
    <>
      <FormProvider {...methods}>
        <Nav />
        <Grid container paddingTop="1rem" paddingX="1rem">
          <Grid size={6}>
            <Stack gap="1rem">
              <CampaignNameEditor register={register('campaignName')} />
              <DimensionSelector
                availableDimensions={availableDimensionSlugs}
                subdimensions={subdimensions}
                subdimensionDictionary={subdimensionDictionary}
                setSubdimensionDictionary={(originalName, newName) =>
                  setSubdimensionDictionary({
                    ...subdimensionDictionary,
                    [originalName]: newName,
                  })
                }
                getFieldPath={getFieldPath}
              />
              {campaign.youtubeMeasurementEnabled ? (
                <YoutubeImpressionsInput
                  name="youtubeImpressions"
                  control={methods.control}
                />
              ) : null}
              <HeaderWithButton
                header="Executive Summary"
                onClick={setDefaultSummaries}
                infoIcon={<TagsInfoIcon />}
              />
              {salesAttributionSummary ? (
                <LciReportFieldInput
                  label="Sales Attribution"
                  name="rsaSummary"
                  characterLimit={500}
                  bulletPointLimit={3}
                  required
                />
              ) : null}
              {hasVisitMetrics ? (
                renderVisitAttributionSection ? (
                  renderVisitAttributionSection()
                ) : (
                  <>
                    <LciReportFieldInput
                      label="Visit Attribution"
                      name="ftaSummary"
                      characterLimit={salesAttributionSummary ? 550 : 1000}
                      bulletPointLimit={salesAttributionSummary ? 3 : 6}
                      required
                    />
                    <HeaderWithButton
                      header="Visit Attribution Key Takeaways"
                      onClick={setDefaultFtaKeyTakeaways}
                      infoIcon={<TagsInfoIcon />}
                    />
                    <LciReportFieldInput
                      label="Overall Results"
                      name="ftaOverallResults"
                      characterLimit={650}
                      bulletPointLimit={4}
                      required
                    />
                    <DimensionFields
                      selectedDimensions={ftaDimensions}
                      control={methods.control}
                    />
                  </>
                )
              ) : null}
              {salesAttributionSummary ? (
                <>
                  <HeaderWithButton
                    header="Sales Attribution Key Takeaways"
                    onClick={setDefaultRsaKeyTakeaways}
                    infoIcon={<TagsInfoIcon />}
                  />
                  <LciReportFieldInput
                    label="Overall Results"
                    name="rsaOverallResults"
                    characterLimit={650}
                    bulletPointLimit={4}
                    required
                  />
                  <LciReportFieldInput
                    label="Contribution Metrics - Overall"
                    name="cmOverall"
                    characterLimit={650}
                    bulletPointLimit={4}
                    minRows={1}
                  />
                  <LciReportFieldInput
                    label="Contribution Metrics - In-Store"
                    name="cmInStore"
                    characterLimit={650}
                    bulletPointLimit={4}
                    minRows={1}
                  />
                  <LciReportFieldInput
                    label="Contribution Metrics - Online"
                    name="cmOnline"
                    characterLimit={650}
                    bulletPointLimit={4}
                    minRows={1}
                  />
                  <LciReportFieldInput
                    label="Summary Table"
                    name="rsaSummaryTable"
                    characterLimit={650}
                    bulletPointLimit={4}
                    minRows={1}
                  />
                  <DimensionFields
                    selectedDimensions={rsaDimensions}
                    control={methods.control}
                  />
                </>
              ) : null}
              <Stack justifyContent="space-between" direction="row">
                <Typography component="h6" variant="h6">
                  Actionable Recommendations
                </Typography>
                <Stack direction="row" spacing={2}>
                  <IconButton
                    size="small"
                    id="help_modal_link"
                    aria-label="help"
                    sx={iconButtonStyles}
                    onClick={removeActionableRecommendation}
                    disabled={actionableRecommendations.length === 0}
                  >
                    <ForwardRefIcon icon={faMinus} />
                  </IconButton>
                  <IconButton
                    size="small"
                    id="help_modal_link"
                    aria-label="help"
                    sx={iconButtonStyles}
                    onClick={addActionableRecommendation}
                    disabled={actionableRecommendations.length >= 4}
                  >
                    <ForwardRefIcon icon={faPlus} />
                  </IconButton>
                </Stack>
              </Stack>
              {actionableRecommendations.map((recommendation, index) => (
                <Stack
                  key={index}
                  justifyContent="space-between"
                  direction="row"
                >
                  <TextField
                    id={`keyLearning${index + 1}`}
                    value={recommendation.keyLearning}
                    onChange={e =>
                      onActionableRecommendationChange(
                        index,
                        e.target.value,
                        true
                      )
                    }
                    sx={{ width: '48%' }}
                    label={`Key Learning ${index + 1}`}
                    inputProps={{ maxLength: 160 }}
                  />
                  <TextField
                    id={`action${index + 1}`}
                    value={recommendation.action}
                    onChange={e =>
                      onActionableRecommendationChange(index, e.target.value)
                    }
                    sx={{ width: '48%' }}
                    label={`Action ${index + 1}`}
                    inputProps={{ maxLength: 160 }}
                  />
                </Stack>
              ))}
              <Typography variant="h6">Included Slides</Typography>
              <IncludedSlidesForm
                hasSalesAttribution={!!salesAttributionSummary}
                hasCampaignAnalytics={!!campaignAnalyticsReport}
              />
              <Typography variant="h6">Brand Logo</Typography>
              <ImageInput image={logoImage} setImage={setLogoImage} />
              <Typography variant="h6">Finish</Typography>
              <Box>
                <Button onClick={() => void downloadPowerPoint()}>
                  Download
                </Button>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </FormProvider>
    </>
  );
};
